import { LAENDER } from "../lib/laender";
import { AdresseFelder, type AdresseFeld } from "./AdresseFelder";
import { Input } from "./components";

/**
 * Der Abschnitt „Für die Rechnung fehlt noch:“ (Einheit D, Mockup freigegeben 26.10.2026), herausgezogen aus dem
 * Bezahlt-Dialog, damit der Knopf „Rechnung senden“ (Einheit E) GENAU DENSELBEN Abschnitt zeigt: Firma (optional),
 * Name, Straße, PLZ, Ort, Land und die E-Mail für die Rechnung. Die Angaben werden beim Kunden gespeichert; Beträge
 * kommen nie von hier.
 */

export type RechnungsFehlt = "adresse" | "land" | "email" | "variante";

/** Der Stand beim Kunden, der den Abschnitt vorbelegt (Antwort der Server-Vorschau bzw. des 422 `rechnungsangaben_fehlen`). */
export interface RechnungsKundenAngaben {
  firma: string | null;
  name: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  email: string | null;
}

export interface RechnungsEingabe {
  firma: string;
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  email: string;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export function eingabeAus(k: RechnungsKundenAngaben | null): RechnungsEingabe {
  return {
    firma: k?.firma ?? "",
    name: k?.name ?? "",
    strasse: k?.strasse ?? "",
    plz: k?.plz ?? "",
    ort: k?.ort ?? "",
    land: k?.land ?? "DE",
    email: k?.email ?? "",
  };
}

/** Name, Straße, PLZ, Ort, Land und eine gültige E-Mail-Adresse sind Pflicht (Firma ist optional). */
export function angabenVollstaendig(e: RechnungsEingabe): boolean {
  return !!e.name.trim() && !!e.strasse.trim() && !!e.plz.trim() && !!e.ort.trim() && !!e.land && EMAIL_RE.test(e.email.trim());
}

/** Der Teil des Request-Bodys, den der Server erwartet (`rechnungsangaben`). Firma wird immer mitgesendet (leer = leeren). */
export function rechnungsangabenBody(e: RechnungsEingabe) {
  return {
    firma: e.firma.trim(),
    name: e.name.trim(),
    strasse: e.strasse.trim(),
    plz: e.plz.trim(),
    ort: e.ort.trim(),
    land: e.land,
    email: e.email.trim(),
  };
}

/** Der 422 `rechnungsangaben_fehlen`: Felder daraus lesen, sonst `null`. */
export function angabenFehlenAusFehler<V = unknown>(err: unknown): { fehlt: RechnungsFehlt[]; kunde: RechnungsKundenAngaben | null; varianten: V[] } | null {
  const e = err as { code?: string; body?: { fehlt?: RechnungsFehlt[]; varianten?: V[]; kunde?: RechnungsKundenAngaben | null } } | null;
  if (!e || e.code !== "rechnungsangaben_fehlen") return null;
  return { fehlt: e.body?.fehlt ?? [], varianten: e.body?.varianten ?? [], kunde: e.body?.kunde ?? null };
}

const FELD_ZU_EINGABE: Partial<Record<AdresseFeld, keyof RechnungsEingabe>> = {
  company: "firma",
  name: "name",
  street: "strasse",
  zip: "plz",
  city: "ort",
  countryCode: "land",
};

export function RechnungsangabenAbschnitt({
  eingabe,
  onChange,
  geprueft,
  testIdPrefix,
}: {
  eingabe: RechnungsEingabe;
  onChange: (e: RechnungsEingabe) => void;
  /** Nach dem ersten Absenden-Versuch: zeigt den Fehlertext, falls etwas fehlt. */
  geprueft: boolean;
  /** `bezahlt` im Bezahlt-Dialog (Testkennungen unverändert), `rechnung` im Dialog „Rechnung senden“. */
  testIdPrefix: string;
}) {
  const ok = angabenVollstaendig(eingabe);
  return (
    <div className="space-y-3 rounded-xl border border-outline-variant/60 p-3.5 dark:border-outline/30" data-testid={`${testIdPrefix}-adresse`}>
      <h3 className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">Für die Rechnung fehlt noch:</h3>
      <AdresseFelder
        werte={{ company: eingabe.firma, name: eingabe.name, street: eingabe.strasse, addition: null, zip: eingabe.plz, city: eingabe.ort, countryCode: eingabe.land }}
        onFeldChange={(feld, wert) => {
          const ziel = FELD_ZU_EINGABE[feld];
          if (ziel) onChange({ ...eingabe, [ziel]: wert });
        }}
        laender={LAENDER}
        firmaName="betreiber"
        zeigeZusatz={false}
        labels={{ firma: "Firma (optional)" }}
        testIdPrefix={testIdPrefix}
      />
      <div>
        <label className="lbl" htmlFor={`${testIdPrefix}-email`}>
          E-Mail für die Rechnung
        </label>
        <Input id={`${testIdPrefix}-email`} type="email" autoComplete="email" value={eingabe.email} onChange={(e) => onChange({ ...eingabe, email: e.target.value })} data-testid={`${testIdPrefix}-email`} />
      </div>
      {geprueft && !ok ? (
        <p className="text-[12px] text-error" role="alert">
          Bitte Name, Straße, PLZ, Ort, Land und eine gültige E-Mail-Adresse angeben.
        </p>
      ) : (
        <p className="text-[12px] text-secondary dark:text-secondary-fixed-dim">Die Angaben werden beim Kunden gespeichert.</p>
      )}
    </div>
  );
}
